// ===========================================================================
// Pipeline View Component
// Visual stage-by-stage breakdown of the detection pipeline
// ===========================================================================

import React, { useRef, useEffect } from 'react';
import { FrameData } from '../types';
import { renderGridToCanvas } from '../utils/rendering';
import './PipelineView.css';

interface PipelineViewProps {
  frameData: FrameData | null;
}

export const PipelineView: React.FC<PipelineViewProps> = ({ frameData }) => {
  const depthCanvasRef = useRef<HTMLCanvasElement>(null);
  const heatmapCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!frameData) return;

    if (depthCanvasRef.current) {
      renderGridToCanvas(frameData.depthGrid, depthCanvasRef.current, 'viridis', true);
    }
    if (heatmapCanvasRef.current) {
      renderGridToCanvas(frameData.heatmapGrid, heatmapCanvasRef.current, 'heat', true);
    }
  }, [frameData]);

  if (!frameData) {
    return (
      <div className="pipeline-view">
        <div className="pipeline-empty">
          <p>Start simulation to see pipeline stages</p>
        </div>
      </div>
    );
  }

  const stages = [
    {
      id: 'detection',
      title: 'Stage 1: Detection',
      icon: '📦',
      description: 'Simulated YOLO detects people & weapons',
      stats: [
        { label: 'People', value: frameData.detections.filter(d => d.cls === 'person').length, highlight: false },
        { label: 'Weapons', value: frameData.detections.filter(d => d.cls === 'weapon').length, highlight: false },
      ],
      status: frameData.detections.length > 0 ? 'active' : 'idle',
    },
    {
      id: 'tracking',
      title: 'Stage 2: Tracking',
      icon: '🔗',
      description: 'DeepSORT-like tracker maintains IDs',
      stats: [
        { label: 'Active', value: frameData.tracks.length, highlight: false },
        { label: 'Confirmed', value: frameData.metrics.confirmedTracks, highlight: false },
        { label: 'Suspects', value: frameData.tracks.filter(t => t.label === 'suspect').length, highlight: false },
      ],
      status: frameData.tracks.length > 0 ? 'active' : 'idle',
    },
    {
      id: 'depth',
      title: 'Stage 3: Depth Proxy',
      icon: '🏔',
      description: 'Synthetic depth from Y-position',
      canvas: depthCanvasRef,
      status: 'active',
    },
    {
      id: 'heatmap',
      title: 'Stage 4: Heatmap',
      icon: '🔥',
      description: 'Gathering centroids → density map',
      canvas: heatmapCanvasRef,
      stats: [
        { label: 'Gatherings', value: frameData.gatherings.length, highlight: false },
      ],
      status: frameData.gatherings.length > 0 ? 'active' : 'idle',
    },
    {
      id: 'contours',
      title: 'Stage 5: Contours & Alerts',
      icon: '⭕',
      description: 'Threshold + morphology → regions',
      stats: [
        { label: 'Regions', value: frameData.crowdRegions.length, highlight: false },
        { label: 'Crowd', value: frameData.flags.crowd ? '⚠️ YES' : 'No', highlight: frameData.flags.crowd },
        { label: 'Mob', value: frameData.flags.mob ? '🔴 YES' : 'No', highlight: frameData.flags.mob },
      ],
      status: frameData.flags.mob ? 'alert' : frameData.flags.crowd ? 'warning' : frameData.crowdRegions.length > 0 ? 'active' : 'idle',
    },
  ];

  return (
    <div className="pipeline-view">
      <div className="pipeline-header">
        <h3>Pipeline Stages</h3>
        <span className="frame-badge">Frame #{frameData.frameIndex}</span>
      </div>
      
      <div className="pipeline-stages">
        {stages.map((stage, index) => (
          <React.Fragment key={stage.id}>
            <div className={`pipeline-stage stage-${stage.status}`}>
              <div className="stage-header">
                <span className="stage-icon">{stage.icon}</span>
                <div className="stage-title-group">
                  <h4 className="stage-title">{stage.title}</h4>
                  <p className="stage-desc">{stage.description}</p>
                </div>
              </div>

              {stage.canvas && (
                <div className="stage-canvas">
                  <canvas ref={stage.canvas} />
                </div>
              )}

              {stage.stats && (
                <div className="stage-stats">
                  {stage.stats.map((stat, i) => (
                    <div 
                      key={i} 
                      className={`stage-stat ${stat.highlight ? 'stat-highlight' : ''}`}
                    >
                      <span className="stat-label">{stat.label}</span>
                      <span className="stat-value">{stat.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {index < stages.length - 1 && (
              <div className="pipeline-arrow">
                <span>↓</span>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Final Output */}
      <div className={`pipeline-output ${frameData.flags.mob ? 'output-mob' : frameData.flags.crowd ? 'output-crowd' : 'output-safe'}`}>
        <div className="output-header">
          <span className="output-icon">
            {frameData.flags.mob ? '🚨' : frameData.flags.crowd ? '⚠️' : '✅'}
          </span>
          <span className="output-title">
            {frameData.flags.mob ? 'MOB ALERT' : frameData.flags.crowd ? 'CROWD DETECTED' : 'ALL CLEAR'}
          </span>
        </div>
        {frameData.flags.weaponGlobal && (
          <div className="weapon-indicator">
            <span>🔫</span> Weapon Present
          </div>
        )}
      </div>
    </div>
  );
};
